import { useState } from 'react'
import { useApiClientInvestment } from '~/@api/client/useApiClientInvestment'
import type { AgreementPreviewRequest } from '~/@pages_investing/order/_order_types/order.types'
import { useMutation, useQuery } from '@tanstack/react-query'
import { getApiErrorMessage } from '~/@lib/utils/error.utils'

type Step = 'agreement' | 'code' | 'success'

function useOrderAgreement(payload: AgreementPreviewRequest) {
  const client = useApiClientInvestment()

  const [step, setStep] = useState<Step>('agreement')

  const agreementPreviewQuery = useQuery({
    queryKey: ['orderAgreement', payload],
    queryFn: () => {
      return client.investment
        .orderControllerGetAgreementPreview(payload, {
          secure: true,
        })
        .then((resp) => resp.data)
    },
  })

  const createOrderVerifyMutation = useMutation({
    mutationFn: () => {
      return client.investment
        .orderControllerCreateStartVerification(payload, {
          secure: true,
        })
        .then((resp) => resp.data)
    },
  })

  const createOrderSignMutation = useMutation({
    mutationFn: (code: string) => {
      return client.investment.orderControllerSignOrder(
        { code, ...payload },
        {
          secure: true,
        }
      )
    },
  })

  return {
    step,
    setStep,

    isAgreementPreviewLoading: agreementPreviewQuery.isLoading,
    agreementPreviewError: getApiErrorMessage(
      agreementPreviewQuery.error,
      'Náhľad zmluvy sa nepodarilo načítať.'
    ),
    // Nový Nest contract vracia jeden preložený agreement (single language,
    // `previewOrder` → `translateObject(..., lang=DEFAULT)`), nie starý pár { sk, lang }.
    agreement: agreementPreviewQuery?.data?.agreement,

    isOrderCreateVerifyLoading: createOrderVerifyMutation.isPending,
    orderCreateVerifyMutation: createOrderVerifyMutation.mutate,
    orderCreateVerifyCodeExpiresAt: createOrderVerifyMutation?.data?.expiresAt
      ? new Date(createOrderVerifyMutation?.data?.expiresAt)
      : null,
    orderCreateVerifyError: getApiErrorMessage(
      createOrderVerifyMutation.error,
      'Overovací kód sa nepodarilo odoslať.'
    ),

    isOrderCreateSignLoading: createOrderSignMutation.isPending,
    orderCreateSignMutation: createOrderSignMutation.mutate,
    orderCreateSignError: getApiErrorMessage(
      createOrderSignMutation.error,
      'Objednávku sa nepodarilo podpísať.'
    ),
  }
}

export { useOrderAgreement }
